import { useEditor, EditorContent, type AnyExtension } from "@tiptap/react";
import StarterKit, { type StarterKitOptions } from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
// Keep default Highlight (emits <mark>). Do NOT add multicolor — the quote-body sanitiser strips style attrs, which would break highlight round-trip.
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { Button } from "@/components/ui/button";
import { useEffect, useRef } from "react";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";

/** User-facing reason for a failed inline-image upload, distinguishing 413/415 from generic failures. */
export function uploadImageErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 413) return "Couldn't upload image — the file is too large.";
    if (err.status === 415) return "Couldn't upload image — unsupported file type.";
  }
  return "Couldn't upload image — please try again.";
}

export interface UploadedImage {
  url: string;
  /** Extra attributes stamped onto the inserted image node (e.g. data-cid, data-qf-src). */
  attrs?: Record<string, string>;
}

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  /** Override the image extension (EmailComposer passes its CID-tagged variant). */
  imageExtension?: AnyExtension;
  onUploadImage?: (file: File) => Promise<UploadedImage>;
  /**
   * Per-consumer StarterKit node toggles. Undefined = full StarterKit (email).
   * The quote-body card passes { heading:false, blockquote:false, codeBlock:false,
   * code:false, horizontalRule:false } so it can only produce tags the server
   * sanitiser + PDF composer support (otherwise those would silently vanish on save).
   */
  starterKitOptions?: Partial<StarterKitOptions>;
}

export function RichTextEditor({ value, onChange, disabled, imageExtension, onUploadImage, starterKitOptions }: RichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ImageExt = imageExtension ?? Image;
  const editor = useEditor({
    extensions: [StarterKit.configure(starterKitOptions ?? {}), Underline, Highlight, ImageExt.configure({ inline: false, allowBase64: false })],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (current !== value) editor.commands.setContent(value, false);
  }, [editor, value]);

  if (!editor) return null;

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !onUploadImage) return;
    // This runs inside a DOM change handler, so a rejected upload would become an
    // unhandled promise rejection — invisible to the user (AppErrorBoundary only
    // catches render errors). Catch it and surface a toast so both the quote
    // Order-items card and the email composer give visible feedback.
    let uploaded: UploadedImage;
    try {
      uploaded = await onUploadImage(file);
    } catch (err) {
      toast.error(uploadImageErrorMessage(err));
      return;
    }
    const { url, attrs } = uploaded;
    editor.chain().focus().insertContent({ type: "image", attrs: { src: url, ...(attrs ?? {}) } }).run();
  };

  const mark = (label: string, active: string, node: React.ReactNode, toggle: () => void) => (
    <Button
      type="button" variant="ghost" size="sm"
      aria-label={label} aria-pressed={editor.isActive(active)} disabled={disabled}
      onClick={toggle}
    >
      {node}
    </Button>
  );

  return (
    <div className="rounded-md border border-input">
      <div className="flex flex-wrap gap-1 border-b border-input p-1">
        {mark("Bold", "bold", <strong>B</strong>, () => editor.chain().focus().toggleBold().run())}
        {mark("Italic", "italic", <em>I</em>, () => editor.chain().focus().toggleItalic().run())}
        {mark("Underline", "underline", <u>U</u>, () => editor.chain().focus().toggleUnderline().run())}
        {mark("Strikethrough", "strike", <s>S</s>, () => editor.chain().focus().toggleStrike().run())}
        {mark("Highlight", "highlight", <span className="bg-yellow-200 px-0.5">H</span>, () => editor.chain().focus().toggleHighlight().run())}
        {mark("Bulleted list", "bulletList", <>&bull; List</>, () => editor.chain().focus().toggleBulletList().run())}
        {mark("Numbered list", "orderedList", <>1. List</>, () => editor.chain().focus().toggleOrderedList().run())}
        {onUploadImage && (
          <>
            <Button
              type="button" variant="ghost" size="sm"
              aria-label="Insert image" disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              Image
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              data-testid="inline-image-input"
              className="hidden"
              disabled={disabled}
              onChange={(event) => { void handleFileChange(event); }}
            />
          </>
        )}
      </div>
      <EditorContent editor={editor} className="min-h-[200px] p-3 prose prose-sm max-w-none focus:outline-none" />
    </div>
  );
}
